import React from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import {
  Alert, Card, Grid, PageHeader, Space, Spin, Table, Tag, Typography,
} from 'antd';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import { useTerminal, useTerminalHistory } from '../../../models/useTerminals';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const fmt = (ts) => (ts && typeof ts.toDate === 'function' ? moment(ts.toDate()).format('YYYY-MM-DD HH:mm:ss') : '—');

const EVENT_COLORS = {
  registered: 'green', bound: 'blue', unbound: 'red', 'mode-changed': 'purple', 'kiosk-armed': 'orange',
};

const modeLabel = (m) => (m === 'tip-kiosk' ? 'Always on' : (m === 'handoff' ? 'Hand-off' : (m || '—')));

/** What is SPECIFIC to this event — the page header already carries the
 * terminal's constant context (team, serial, tmr), so rows never repeat it.
 * Binding events name their target (that's their payload — it can differ
 * across epochs); mode changes show the transition; arms have no payload. */
const eventDetail = (row) => {
  switch (row.event) {
    case 'mode-changed':
      return `${modeLabel(row.previousMode)} → ${modeLabel(row.mode)}`;
    case 'bound':
      return `→ ${row.teamName || row.teamId || '—'} · ${row.deviceTagId || row.tagId || ''}`.trim();
    case 'unbound':
      return `from ${row.teamName || row.teamId || '—'}`;
    case 'registered':
      return row.deviceTagId || row.tagId || '';
    default:
      return '';
  }
};

/** /admin/ops/terminals/:serialNumber — one device's full biography as a
 * scannable table (the row preview shows the timeline; this is the record). */
const AdminTerminalDetailScreen = () => {
  const { serialNumber } = useParams();
  const [docLoading, terminal, docError] = useTerminal(serialNumber);
  const [historyLoading, events, historyError] = useTerminalHistory(serialNumber, 200);
  const screens = useBreakpoint();
  // Rule 10: only an EXPLICIT false is compact — {} on the first tick stays desktop.
  const compact = screens.md === false;

  const columns = [
    {
      title: 'At',
      key: 'at',
      width: compact ? 96 : 170,
      render: (row) => (compact
        ? (
          <Space direction="vertical" size={0} style={{ fontVariantNumeric: 'tabular-nums' }}>
            <Text style={{ fontSize: '12px' }}>{fmt(row.at).slice(0, 10)}</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>{fmt(row.at).slice(11)}</Text>
          </Space>
        )
        : fmt(row.at)),
    },
    {
      title: 'Event',
      key: 'event',
      render: (row) => (
        <Space direction="vertical" size={2}>
          <Tag color={EVENT_COLORS[row.event] || 'default'} style={{ margin: 0 }}>{row.event}</Tag>
          {compact && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {[eventDetail(row) || null,
                (row.byName || row.by) && `by ${row.byName || row.by}${row.byName ? ` via ${row.by}` : ''}`,
              ].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Detail',
      key: 'detail',
      responsive: ['md'],
      render: (row) => <Text type="secondary">{eventDetail(row) || '—'}</Text>,
    },
    {
      title: 'By',
      key: 'by',
      width: 160,
      responsive: ['md'],
      render: (row) => (
        <Space direction="vertical" size={0}>
          <Text>{row.byName || row.by || '—'}</Text>
          {row.byName && <Text type="secondary" style={{ fontSize: '11px' }}>{`via ${row.by}`}</Text>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', textAlign: 'center' }}>
      <PageHeader
        ghost={false}
        title={`Terminal —${String(serialNumber || '').slice(-4).toUpperCase()}`}
        subTitle={serialNumber}
        onBack={() => window.history.back()}
      />
      {(docError || historyError) && (
        <Alert type="error" showIcon message={docError || historyError} style={{ margin: '12px' }} />
      )}
      <Card style={{ margin: '12px', textAlign: 'left' }}>
        {docLoading ? <Spin size="small" /> : (
          <Space size="large" wrap>
            <Text strong>{(terminal && (terminal.teamName || terminal.teamId)) || 'Unbound'}</Text>
            {terminal && (terminal.mode === 'tip-kiosk'
              ? <Tag color="purple">Always on</Tag> : <Tag>Hand-off</Tag>)}
            {terminal && <Text type="secondary">{`tmr ${terminal.tmr || '—'}`}</Text>}
            {terminal && <Text type="secondary">{`last armed ${fmt(terminal.lastArmedAt)} by ${terminal.lastArmedBy || '—'}`}</Text>}
            {terminal && <Text type="secondary">{`last seen ${fmt(terminal.lastSeenAt)}`}</Text>}
          </Space>
        )}
      </Card>
      <Table
        rowKey="id"
        loading={historyLoading}
        dataSource={events}
        columns={columns}
        pagination={{ pageSize: 50 }}
        style={{ margin: '12px' }}
      />
    </div>
  );
};

export default AdminTerminalDetailScreen;
